import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import Modal from '../../components/ui/Modal';
import Input, { Select, Textarea } from '../../components/ui/Input';
import StatusBadge from '../../components/shared/StatusBadge';
import Badge from '../../components/ui/Badge';
import Table, { THead, TH, TBody, TR, TD, Pagination } from '../../components/ui/Table';
import { timeAgo } from '../../utils/formatDate';
import toast from 'react-hot-toast';

const PRIORITY_COLORS = { low: 'gray', medium: 'blue', high: 'yellow', critical: 'red' };

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });
  const [creating, setCreating] = useState(false);

  const loadTickets = async () => {
    try {
      const { data } = await api.get(`/tickets?page=${page}&limit=15`);
      setTickets(data.tickets);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Load tickets error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTickets(); }, [page]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/tickets', form);
      toast.success('Ticket created');
      setShowCreate(false);
      setForm({ subject: '', description: '', category: 'general', priority: 'medium' });
      loadTickets();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] text-[#475569] mb-[2px]">Service & Support</div>
          <h2 className="text-[22px] font-bold text-[#e2e8f0] tracking-[-0.3px]">Support Tickets</h2>
          <p className="text-[13px] text-[#64748b] mt-[2px]">Get help from our engineering team</p>
        </div>
        {tickets.length > 0 && (
          <button
            className="px-4 py-2 rounded-lg text-[12.5px] font-semibold bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7] transition-all"
            onClick={() => setShowCreate(true)}
          >
            + New Ticket
          </button>
        )}
      </div>

      {tickets.length > 0 ? (
        <>
          <Table>
            <THead>
              <tr>
                <TH>Ticket</TH>
                <TH>Subject</TH>
                <TH>Category</TH>
                <TH>Priority</TH>
                <TH>Status</TH>
                <TH>Replies</TH>
                <TH>Created</TH>
              </tr>
            </THead>
            <TBody>
              {tickets.map(t => (
                <TR key={t.id} onClick={() => window.location.href = `/central/support/${t.id}`}>
                  <TD><span className="font-mono text-[#06b6d4]">{t.ticketNumber}</span></TD>
                  <TD><span className="text-[#e2e8f0]">{t.subject}</span></TD>
                  <TD><span className="capitalize text-[#94a3b8]">{t.category}</span></TD>
                  <TD><Badge color={PRIORITY_COLORS[t.priority]}>{t.priority}</Badge></TD>
                  <TD><StatusBadge status={t.status} /></TD>
                  <TD>{t._count?.messages || 0}</TD>
                  <TD>{timeAgo(t.createdAt)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      ) : (
        <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px]">
          <div className="text-center py-12 px-8">
            <svg className="w-11 h-11 mx-auto mb-[10px] text-[#475569] opacity-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            <h3 className="text-[16px] font-semibold text-[#e2e8f0] mb-1">No support tickets</h3>
            <p className="text-[12.5px] text-[#475569] mb-4">Need help? Our team responds within 24 hours.</p>
            <button
              className="px-7 py-[10px] rounded-lg text-[12.5px] font-semibold bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7] transition-all"
              onClick={() => setShowCreate(true)}
            >
              New Ticket
            </button>
          </div>
        </div>
      )}

      {/* Create ticket modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New Support Ticket" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Subject"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Brief description of the issue"
            required
          />
          <Textarea
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Provide details about your issue..."
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {['hardware', 'software', 'license', 'warranty', 'account', 'feature', 'general'].map(c => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </Select>
            <Select
              label="Priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            >
              {['low', 'medium', 'high', 'critical'].map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </Select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg text-sm font-medium border border-[rgba(148,163,184,0.10)] text-[#94a3b8] hover:text-[#e2e8f0] transition-colors">Cancel</button>
            <button type="submit" disabled={creating} className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7] transition-all disabled:opacity-50">{creating ? 'Creating...' : 'Create Ticket'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
